/** @format */

import useCardGallery from '@/@ducks/cardRoom/hooks/useCardGallery';
import useGalleryModal from '@/@ducks/cardRoom/hooks/useGalleryModal';
import { GTMListingCard } from '@/@ducks/googleTagManager/helpers/google-tag-manager';
import GalleryIcon from '@/assets/icons/card-room-gallery-icon.svg';
import MapIcon from '@/assets/icons/card-room-map-pin-icon.svg';
import PlanimetryIcon from '@/assets/icons/card-room-planimetry-icon.svg';
import VideoIcon from '@/assets/icons/card-room-video-icon.svg';
import { Icon } from '@/components/ui/Icon';
import { TTranslation } from '@/i18n/createProvider';
import { useAppTranslation } from '@/i18n/declarations';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCardRoomContext } from './context';
import { CardLink, CardTag, CardTagsContainer, DefaultIcon, GalleryIcons, Image, Wrapper } from './Gallery.style';
import GalleryCarousel from './GalleryCarousel';
import { TabsModal } from './TabsModal';
import { Tab } from './TabsModal/types';

const INITIAL_TABS: Tab[] = ['gallery', 'planimetry', 'vr', 'map'];

const Gallery: React.FC<GalleryProps> = ({ gtmSelectItem }) => {
  const { t } = useAppTranslation();
  const { url, room, assetNotes, buildingType } = useCardRoomContext();
  const { media, code } = room!;
  const { photosLowQuality, photosHighQuality, hasVR, hasMultiplePhotos, hasPlanimetry } = useCardGallery({ media });
  const { isShow, show, hide, GalleryModal } = useGalleryModal();

  const [activeTab, setActiveTab] = useState<Tab>(null);

  const showTab = (tab: Tab) => {
    setActiveTab(tab);
    show();

    if (tab !== null) {
      GTMListingCard(code, tab);
    }
  };

  const getIcon = useCallback((tab: Tab) => {
    switch (tab) {
      case 'gallery':
        return GalleryIcon;
      case 'videos':
        return VideoIcon;
      case 'planimetry':
        return PlanimetryIcon;
      case 'map':
        return MapIcon;
    }
  }, []);

  const getIconContent = useCallback(
    (tab: Tab) => {
      const icon = getIcon(tab);
      if (tab === 'vr') return <span>3D</span>;
      return <Icon key={tab} icon={icon} width={20} height={20} />;
    },
    [getIcon],
  );

  useEffect(() => {
    document.body.style.overflowY = isShow ? 'hidden' : 'initial';
  }, [isShow]);

  const tagsList = useMemo(() => {
    const tags: string[] = [];
    // TODO: re-enable asset notes when we have that info
    // const tags: string[] = assetNotes.map(assetNote => assetNote[intl.locale as 'it' | 'en']);

    if (buildingType === 'Building') {
      tags.push(t('plp.tags.coliving_buildings' as TTranslation));
    }

    if (room?.flatFeatures.tags.includes('female-only')) {
      tags.push(t('plp.filters.suitability.female_only' as TTranslation));
    }
    return tags;
  }, [assetNotes, buildingType]);

  const tabsList = useMemo(() => {
    let tabs = INITIAL_TABS;
    if (!hasMultiplePhotos) tabs = tabs.filter(tab => tab !== 'gallery');
    if (!hasVR) tabs = tabs.filter(tab => tab !== 'vr');
    if (!hasPlanimetry) tabs = tabs.filter(tab => tab !== 'planimetry');

    return tabs;
  }, [hasMultiplePhotos, hasVR, hasPlanimetry]);

  const handleClickLink = () => {
    if (gtmSelectItem) {
      gtmSelectItem();
    }
  };

  return (
    <Wrapper>
      <CardLink to={url} onClick={handleClickLink}>
        {hasMultiplePhotos ? (
          <GalleryCarousel photos={photosLowQuality} />
        ) : (
          <Image src={photosHighQuality[0]} alt={t('pdp.representative_image' as TTranslation)} />
        )}
      </CardLink>

      <GalleryModal isShow={isShow} onClose={hide}>
        {isShow && <TabsModal tabs={tabsList} activeTab={activeTab} switchTab={setActiveTab} close={hide} />}
      </GalleryModal>

      <GalleryIcons>
        {tabsList.map(tab => (
          <DefaultIcon key={tab} onClick={() => showTab(tab)} aria-label={t(`aria_label_text.${tab}` as TTranslation)}>
            {getIconContent(tab)}
          </DefaultIcon>
        ))}
      </GalleryIcons>

      <CardTagsContainer>
        {tagsList.map(tag => (
          <CardTag>{tag}</CardTag>
        ))}
      </CardTagsContainer>
    </Wrapper>
  );
};

interface GalleryProps {
  gtmSelectItem?: Function;
}

export default Gallery;
